<template>
	<v-menu
		@input="(valor) => $emit('input', valor)"
		:value="mostra"
		v-bind="$attrs"
	>
		<v-card
			color="grey lighten-4"
			flat
			width="310"
		>
			<v-toolbar
				:color="obterCorDoEvento(agenda.tipoDaLabel)(evento)"
				dark
			>
				<v-list-item-content>
					<v-list-item-title class="headline">{{evento.tipoDaSessao.nome}}</v-list-item-title>
					<v-list-item-subtitle>
						{{evento.entidade.nome}}
						<template v-if="evento.grauDaSessao">&mdash; {{ evento.grauDaSessao }} </template>
					</v-list-item-subtitle>
				</v-list-item-content>
				<IconeDoEvento
					:evento="evento" style="width: 35px;"
					class="mr-n2"
				/>
				<ComponentAuthGuard :requires="requires">
					<v-btn icon
						@click="() => $router.push({
							name: 'editar-evento',
							params: { id: evento._id },
						})"
						class="pl-4"
					>
						<v-icon>mdi-pencil</v-icon>
					</v-btn>
					<v-menu
						offset-y
						bottom
						left
					>
						<template #activator="{ on }">
							<v-btn icon v-on="on">
								<v-icon>mdi-dots-vertical</v-icon>
							</v-btn>
						</template>
						<v-list>
							<v-dialog v-model="dialogoExclusao" width="300">
								<template #activator="{ on }">
									<v-list-item v-on="on">
										<v-list-item-icon>
											<v-icon color="red"> mdi-delete </v-icon>
										</v-list-item-icon>
										<v-list-item-content>
											<v-list-item-title class="red--text">
												Remover
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</template>
								<CardConfirma
									@cancel="() => dialogoExclusao = false"
									@confirm="() => excluirEvento()"
									:carregando="excluindo"
									:title="`Remover evento?`"
								/>
							</v-dialog>
						</v-list>
					</v-menu>
				</ComponentAuthGuard>
			</v-toolbar>
			<v-card-text>
				<div>
					<span> {{ formatarData(evento.data) }} </span>
					<span v-if="evento.horario || eventoDeLoja(evento) && evento.entidade.horarioDaSessao"> às {{ evento.horario || (eventoDeLoja(evento) ? evento.entidade.horarioDaSessao : undefined) }}</span>
				</div>
				<v-divider />
				<TextoComLinks :texto="evento.texto" class="conteudo-do-evento mb-4" />
				<a
					v-if="eventoDeLoja(evento)"
					:href="`https://www.google.com/maps/search/?api=1&query=${evento.entidade.endereco}`"
					target="_blank"
				>
					<div class="text-truncate">
						<v-icon small class="mr-1">mdi-map-marker</v-icon>
						<span class="text-truncate"> {{ evento.entidade.endereco }}</span>
					</div>
				</a>
			</v-card-text>
			<template v-if="mostraPresenca">
				<AppLoading v-if="buscandoPresenca" />
				<v-card-actions
					class="flex-column align-start pt-0"
					v-else-if="!passouDaData && presenca"
				>
					<BotoesDePresenca
						v-model="presenca"
						:evento="evento"
					/>
				</v-card-actions>
			</template>
			<v-divider />
			<v-card-actions class="justify-space-between">
				<v-btn
					text
					color="primary"
					@click="() => copiarLink()"
				>Copiar link</v-btn>
				<v-btn
					color="primary"
					:to="{
						name: 'detalhar-evento',
						params: { id: evento._id },
					}"
					text
				>
					Detalhes
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script lang="ts">
export default {
	model: {
		prop: 'mostra',
	},
}
</script>

<script setup lang="ts">
import { computed, PropType, ref, watch } from 'vue'
import { IEvento } from '../../../common/types/Evento'
import ComponentAuthGuard from '@/components/ComponentAuthGuard.vue'
import CardConfirma from '@/components/CardConfirma.vue'
import { criarPresenca, irmaoPodeVerPresenca, obterCorDoEvento } from '../../shareds/sharedsEvento'
import { agenda, authModule, userModule, alert } from '@/store'
import { formatarData } from '../../shareds/formatadores'
import EventoService from '@/services/EventoService'
import { adminPotenciaGuard } from '@/shareds/sharedsPotencia'
import { adminLojaGuard } from '@/shareds/sharedsLoja'
import IconeDoEvento from '@/components/IconeDoEvento.vue'
import { InputPresenca } from '../../../common/types/Presenca'
import PresencaService from '@/services/PresencaService'
import BotoesDePresenca from './BotoesDePresenca.vue'
import copyToClipboard from 'copy-to-clipboard'
import { dataEHoraDoEvento, eventoDeLoja } from '../../../common/shareds/sharedEvento'
import dayjs from 'dayjs'
import TextoComLinks from '@/components/TextoComLinks.vue'

const props = defineProps({
	mostra: { default: false, type: Boolean },
	evento: { type: Object as PropType<IEvento>, required: true },
})

const emit = defineEmits<{
	(e: 'input', mostra: boolean): void
	(e: 'exclusao', evento: IEvento): void
}>()

const dialogoExclusao = ref(false)
const excluindo = ref(false)

const presenca = ref<InputPresenca | null>(null)
const buscandoPresenca = ref(true)

const requires = computed(() => {
	switch (props.evento.tipoDaSessao.abreviacao) {
		case 'FDJ':
		case 'DEMOL':
			const { currentUser } = userModule
			if (currentUser && currentUser.role !== 'Visitante' && currentUser.secretarioDeInformatica) return true
	}
	switch (props.evento.tipoDaSessao.tipoDaEntidade) {
		case 'somar': return ['admin']
		case 'potencia': return ['admin', adminPotenciaGuard(props.evento.entidade._id)]
		case 'loja': return ['admin', adminLojaGuard(props.evento.entidade._id)]
	}
})

const passouDaData = computed(() => dayjs().diff(dataEHoraDoEvento(props.evento), 'hours') >= 12)

const mostraPresenca = computed(() => irmaoPodeVerPresenca(props.evento, userModule.currentUser))

async function excluirEvento() {
	try {
		excluindo.value = true
		await EventoService.remove(props.evento._id)
	} catch (error) {
		alert.setError(error)
	} finally {
		excluindo.value = false
		dialogoExclusao.value = false
		emit('exclusao', props.evento)
		emit('input', false)
	}
}

function copiarLink(){
	const copiado = copyToClipboard(`${window.location.origin}/evento/${props.evento._id}`)
	if (copiado) alert.setSuccess('Copiado com sucesso')
}

async function buscarPresenca() {
	const usuario = userModule.currentUser
	if (!usuario) return
	try {
		buscandoPresenca.value = true
		const presencaDoIrmao = usuario.role !== 'Visitante'
			? await PresencaService.getByEventoEIrmao(props.evento._id, usuario._id)
			: null

		presenca.value = presencaDoIrmao
			? {
				...presencaDoIrmao,
				irmao: presencaDoIrmao.irmao._id,
			}
			: criarPresenca({
				evento: props.evento._id,
				irmao: usuario._id,
				intencoes: [],
			})
	} catch (error) {
		alert.setError(error)
	} finally {
		buscandoPresenca.value = false
	}
}

watch(() => props.mostra, (mostra) => {
	if (mostra) {
		buscarPresenca()
	}
}, { immediate: true })
</script>

<style lang="css" scoped>
.conteudo-do-evento {
	display: -webkit-box;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	line-clamp: 4;
	-webkit-line-clamp: 4;
}
</style>
